class ShopByCut extends HTMLElement {
  connectedCallback() {
    this.preview = this.querySelector('[data-sds-preview]');
    this.items = Array.from(this.querySelectorAll('[data-sds-shape]'));
    if (!this.preview) return;

    this.link = this.preview.querySelector('[data-sds-product-link]');
    this.slidesEl = this.preview.querySelector('[data-sds-slides]');
    this.dotsEl = this.preview.querySelector('[data-sds-dots]');
    this.prevBtn = this.preview.querySelector('[data-sds-prev]');
    this.nextBtn = this.preview.querySelector('[data-sds-next]');

    this.index = 0;
    this._onEnter = this._onEnter.bind(this);
    this._onFocus = this._onFocus.bind(this);
    this._onSelect = this._onSelect.bind(this);
    this._onPrev = this._onPrev.bind(this);
    this._onNext = this._onNext.bind(this);
    this._onDot = this._onDot.bind(this);
    this._stopNav = this._stopNav.bind(this);

    this.items.forEach((item) => {
      item.addEventListener('mouseenter', this._onEnter);
      item.addEventListener('focusin', this._onFocus);
      item.addEventListener('click', this._onSelect);
    });

    this.prevBtn?.addEventListener('click', this._onPrev);
    this.nextBtn?.addEventListener('click', this._onNext);
    this.dotsEl?.addEventListener('click', this._onDot);
    this.prevBtn?.addEventListener('click', this._stopNav);
    this.nextBtn?.addEventListener('click', this._stopNav);
    this.dotsEl?.addEventListener('click', this._stopNav);

    this.activeItem = this.items.find((el) => el.classList.contains('is-active')) || this.items[0];
    this._syncNav();
  }

  disconnectedCallback() {
    this.items?.forEach((item) => {
      item.removeEventListener('mouseenter', this._onEnter);
      item.removeEventListener('focusin', this._onFocus);
      item.removeEventListener('click', this._onSelect);
    });
    this.prevBtn?.removeEventListener('click', this._onPrev);
    this.nextBtn?.removeEventListener('click', this._onNext);
    this.dotsEl?.removeEventListener('click', this._onDot);
    this.prevBtn?.removeEventListener('click', this._stopNav);
    this.nextBtn?.removeEventListener('click', this._stopNav);
    this.dotsEl?.removeEventListener('click', this._stopNav);
  }

  _stopNav(event) {
    event.preventDefault();
    event.stopPropagation();
  }

  _onEnter(event) {
    this._activate(event.currentTarget);
  }

  _onFocus(event) {
    const item = event.currentTarget;
    if (item) this._activate(item);
  }

  _onSelect(event) {
    event.preventDefault();
    this._activate(event.currentTarget);
  }

  _onPrev(event) {
    this._stopNav(event);
    this._goTo(this.index - 1);
  }

  _onNext(event) {
    this._stopNav(event);
    this._goTo(this.index + 1);
  }

  _onDot(event) {
    const btn = event.target.closest('[data-sds-dot]');
    if (!btn) return;
    this._stopNav(event);
    const next = Number(btn.getAttribute('data-index'));
    if (!Number.isNaN(next)) this._goTo(next);
  }

  _activate(item) {
    if (!item || item === this.activeItem) return;

    this.items.forEach((el) => {
      el.classList.toggle('is-active', el === item);
      const card = el.querySelector('.sds__card');
      if (card) card.setAttribute('aria-pressed', el === item ? 'true' : 'false');
    });
    this.activeItem = item;

    if (window.matchMedia('(max-width: 480px)').matches) {
      item.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
    }

    const url = item.getAttribute('data-product-url') || '';
    this._setProductLink(url, item.getAttribute('data-preview-alt') || '');

    const raw = item.getAttribute('data-preview-images');
    if (!raw) return;

    let images = [];
    try {
      images = JSON.parse(raw);
    } catch (_) {
      return;
    }

    if (!Array.isArray(images) || !images.length) return;
    this._renderSlides(images);
  }

  _setProductLink(url, alt) {
    if (!this.link) return;

    if (url) {
      if (this.link.tagName !== 'A') {
        const anchor = document.createElement('a');
        anchor.className = this.link.className;
        anchor.setAttribute('data-sds-product-link', '');
        while (this.link.firstChild) anchor.appendChild(this.link.firstChild);
        this.link.replaceWith(anchor);
        this.link = anchor;
      }
      this.link.href = url;
      this.link.setAttribute('aria-label', alt || 'View product');
    } else if (this.link.tagName === 'A') {
      const div = document.createElement('div');
      div.className = this.link.className;
      div.setAttribute('data-sds-product-link', '');
      while (this.link.firstChild) div.appendChild(this.link.firstChild);
      this.link.replaceWith(div);
      this.link = div;
      this.slidesEl = this.preview.querySelector('[data-sds-slides]');
    }
  }

  _renderSlides(images) {
    if (!this.slidesEl) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!reduceMotion) this.slidesEl.classList.add('is-fading');

    const apply = () => {
      this.slidesEl.innerHTML = images
        .map((image, i) => {
          const src = image.src || '';
          const srcset = image.srcset || '';
          const alt = image.alt || '';
          return `
            <div class="sds__slide${i === 0 ? ' is-active' : ''}" data-sds-slide>
              <img
                class="sds__media-img"
                src="${src}"
                ${srcset ? `srcset="${srcset}"` : ''}
                sizes="(min-width: 990px) 42vw, 100vw"
                alt="${this._escape(alt)}"
                loading="${i === 0 ? 'eager' : 'lazy'}"
                decoding="async"
              />
            </div>
          `;
        })
        .join('');

      if (this.dotsEl) {
        this.dotsEl.innerHTML = images
          .map(
            (_, i) => `
              <button
                type="button"
                class="sds__dot${i === 0 ? ' is-active' : ''}"
                data-sds-dot
                data-index="${i}"
                aria-label="Image ${i + 1}"
                ${i === 0 ? 'aria-current="true"' : ''}
              ></button>
            `
          )
          .join('');
      }

      this.index = 0;
      this._syncNav();
      this.slidesEl.classList.remove('is-fading');
    };

    if (reduceMotion) {
      apply();
      return;
    }

    window.setTimeout(apply, 160);
  }

  _goTo(next) {
    const slides = Array.from(this.slidesEl?.querySelectorAll('[data-sds-slide]') || []);
    if (slides.length < 2) return;

    const total = slides.length;
    this.index = ((next % total) + total) % total;

    slides.forEach((slide, i) => slide.classList.toggle('is-active', i === this.index));

    const dots = Array.from(this.dotsEl?.querySelectorAll('[data-sds-dot]') || []);
    dots.forEach((dot, i) => {
      const active = i === this.index;
      dot.classList.toggle('is-active', active);
      if (active) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
  }

  _syncNav() {
    const count = this.slidesEl?.querySelectorAll('[data-sds-slide]').length || 0;
    const show = count > 1;
    this.prevBtn?.toggleAttribute('hidden', !show);
    this.nextBtn?.toggleAttribute('hidden', !show);
    this.dotsEl?.toggleAttribute('hidden', !show);
  }

  _escape(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }
}

if (!customElements.get('shop-by-cut')) {
  customElements.define('shop-by-cut', ShopByCut);
}
